import { useZervantSubscriptionDetails } from '@features/zervant-subscription/hooks/useZervantSubscriptionDetails'
import { SubscriptionPlan } from '@modules-deprecated/app/organization/enums/subscriptionPlan'

import { useOrganizationMarket, useOrganizationSubscription } from '../../../../../../../hooks'
import { useOrganizationUiSetup } from '../../../../../../../hooks/useOrganizationUiSetup'
import { useIsAnnualReportAvailable } from '../../../../../../annualReport/hooks/useIsAnnualReportAvailable'
import { getVisiblePlans } from '../../../utils/getVisiblePlans'

export const useShouldDisplayPlan = () => {
  const { subscriptionPlan, isTrial, isLocked } = useOrganizationSubscription()
  const { market } = useOrganizationMarket()
  const isAnnualReportAvailable = useIsAnnualReportAvailable()
  const { isUiSetupShineInvoicingAccounting } = useOrganizationUiSetup()
  const { isInZervantNoPlan } = useZervantSubscriptionDetails()

  const isLockedNonTrial = !!isLocked && !isTrial
  let assumedPlan = subscriptionPlan

  // Locked (non-trial) orgs are always offered the full paid lineup, so we
  // assume they're on the base paid plan (BasicPaid/Start) for visibility.
  if (isLockedNonTrial) {
    assumedPlan = SubscriptionPlan.BasicPaid
  } else if (isTrial) {
    // For trial subscriptions, billingType is considered Own, so subscriptionPlan
    // comes from organization.subscriptionPlan rather than organization.subscriptionProductPlan
    // and doesn't reliably reflect the trial tier — assume PremiumTrial to resolve visibility.
    // For market fr/nl/de, PremiumTrial is aliased to PlusTrial in the catalog, so this also
    // covers PlusTrial's market-specific upgrade path in those markets.
    assumedPlan = SubscriptionPlan.PremiumTrial
  } else if (isUiSetupShineInvoicingAccounting && isInZervantNoPlan) {
    assumedPlan = SubscriptionPlan.Free
  }

  const rawVisiblePlans = getVisiblePlans(assumedPlan ?? SubscriptionPlan.Free, market || undefined)
  const visiblePlans = isAnnualReportAvailable
    ? rawVisiblePlans
    : rawVisiblePlans.filter((plan) => plan !== SubscriptionPlan.Complete)

  return {
    shouldDisplayBasicPlan: visiblePlans.includes(SubscriptionPlan.BasicPaid),
    shouldDisplayStartPlan: visiblePlans.includes(SubscriptionPlan.Start),
    shouldDisplayPlusPlan: visiblePlans.includes(SubscriptionPlan.Plus),
    shouldDisplayCompletePlan: visiblePlans.includes(SubscriptionPlan.Complete),
    shouldDisplayFreePlan:
      isLocked || visiblePlans.includes(SubscriptionPlan.Free) || visiblePlans.includes(SubscriptionPlan.Basic),
  }
}
